import { useThemeStore } from '../../stores/themeStore';
import { Moon, Sparkles, Sun } from 'lucide-react';
import { useUserData } from '../../hooks/useUserData';
import { getRoleLabels } from '../../utils/roles';
import { APP_THEMES } from '../../stores/themeStore';
import { APP_LANGUAGES, getAppText, useLanguageStore } from '../../stores/languageStore';

export const Header = ({ title, subtitle, actions, showUserPanel = true }) => {
  const { isDarkMode, appTheme, toggleDarkMode, setAppTheme } = useThemeStore();
  const { appLanguage, setAppLanguage } = useLanguageStore();
  const { userData } = useUserData();

  const roleLabels = getRoleLabels(userData);
  const t = getAppText(appLanguage);

  return (
    <header className="app-topbar">
      <div className="app-topbar-inner">
        <div className="rounded-2xl border border-white/60 dark:border-neutral-700/80 bg-white/70 dark:bg-neutral-900/60 px-3 py-2.5 backdrop-blur-xl shadow-[0_16px_34px_-24px_rgba(15,23,42,0.45)]">
          <div className={`flex flex-wrap items-center gap-2 ${showUserPanel ? 'md:justify-between' : 'justify-end'}`}>
            {showUserPanel && (
              <div className="flex items-start gap-2.5">
                <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-primary-200 bg-primary-100/80 text-primary-700 dark:border-primary-800 dark:bg-primary-900/40 dark:text-primary-300">
                  <Sparkles className="h-4.5 w-4.5" />
                </span>
                <div>
                  <h1 className="text-lg md:text-xl font-bold tracking-tight text-neutral-900 dark:text-white">{title}</h1>
                  {subtitle && (
                    <p className="text-neutral-600 dark:text-neutral-300 mt-0.5 text-xs md:text-sm">{subtitle}</p>
                  )}
                </div>
              </div>
            )}

            <div className="flex flex-wrap justify-end gap-2 items-center">
              {actions}
              <label className="flex items-center gap-2 px-3 py-2 bg-white/75 dark:bg-neutral-800/80 rounded-xl min-h-[44px] border border-neutral-200/80 dark:border-neutral-700/80">
                <span className="text-[11px] uppercase tracking-[0.16em] text-neutral-500 dark:text-neutral-400">{t.language}</span>
                <select
                  value={appLanguage}
                  onChange={(event) => setAppLanguage(event.target.value)}
                  className="min-h-[32px] rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white/90 dark:bg-neutral-900 px-2 py-1 text-xs font-semibold text-neutral-800 dark:text-neutral-100"
                  aria-label="Wybór języka aplikacji"
                >
                  {APP_LANGUAGES.map((language) => (
                    <option key={language.value} value={language.value}>{language.label}</option>
                  ))}
                </select>
              </label>
              <label className="flex items-center gap-2 px-3 py-2 bg-white/75 dark:bg-neutral-800/80 rounded-xl min-h-[44px] border border-neutral-200/80 dark:border-neutral-700/80">
                <span className="text-[11px] uppercase tracking-[0.16em] text-neutral-500 dark:text-neutral-400">{t.theme}</span>
                <select
                  value={appTheme}
                  onChange={(event) => setAppTheme(event.target.value)}
                  className="min-h-[32px] rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white/90 dark:bg-neutral-900 px-2 py-1 text-xs font-semibold text-neutral-800 dark:text-neutral-100"
                  aria-label="Wybór motywu kolorystycznego"
                >
                  {APP_THEMES.map((theme) => (
                    <option key={theme.value} value={theme.value}>{theme.label}</option>
                  ))}
                </select>
              </label>
              {roleLabels.length > 0 && (
                <div className="flex items-center gap-2 px-3 py-2 bg-white/75 dark:bg-neutral-800/80 rounded-xl min-h-[44px] border border-neutral-200/80 dark:border-neutral-700/80">
                  <span className="text-[11px] uppercase tracking-[0.16em] text-neutral-500 dark:text-neutral-400">{t.roles}</span>
                  <span className="text-xs sm:text-sm font-semibold text-neutral-800 dark:text-neutral-100">{roleLabels.join(' • ')}</span>
                </div>
              )}
              <button
                onClick={toggleDarkMode}
                className="px-3 py-2 rounded-xl hover:bg-neutral-200/70 dark:hover:bg-neutral-700/70 transition-colors min-h-[44px] min-w-[44px] border border-neutral-200/80 dark:border-neutral-700/80 bg-white/80 dark:bg-neutral-800/80"
                aria-label="Toggle dark mode"
              >
                {isDarkMode ? (
                  <Sun className="w-5 h-5 text-amber-500" />
                ) : (
                  <Moon className="w-5 h-5 text-neutral-700" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};
